/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string} [option1]
 * @property {string[]} [options]
 */

/**
 * <protection-upsell>
 * Keeps each "Protection size summary" block in sync with the main product's
 * selected size, using the "main:protection" mapping on the host element.
 *
 * Per summary block:
 *  - fills "For product size" / "Protection size" text
 *  - pre-selects the mapped size on the hidden variant input + <select>
 *  - "Choose different size" reveals the native <select>; choosing a size
 *    submits the product form (adds that variant to cart)
 *  - the Add to cart button adds the mapped size
 */
class ProtectionUpsell extends HTMLElement {
  /** @type {Map<string, string>} */
  mapping = new Map();

  /** @type {string} */
  mainSize = '';

  /** @type {HTMLElement[]} */
  summaries = [];

  /** @type {(event: Event) => void} */
  #onVariantUpdate = (event) => {
    const variant = /** @type {CustomEvent<{ resource?: ProductVariant }>} */ (event).detail?.resource;
    if (!variant) return;
    const size = this.#sizeFromVariant(variant);
    if (size) {
      this.mainSize = size;
      this.#applyMainSize(size);
    }
  };

  connectedCallback() {
    this.mapping = this.#parseMapping(this.getAttribute('data-protection-mapping'));
    this.mainSize = (this.getAttribute('data-main-size') || '').trim();
    this.summaries = Array.from(this.querySelectorAll('protection-size-summary'));
    this.#bindSummaries();
    this.#applyMainSize(this.mainSize);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {string | null} str
   * @returns {Map<string, string>}
   */
  #parseMapping(str) {
    const map = new Map();
    if (!str) return map;
    for (const pair of str.split(',')) {
      const [key, value] = pair.split(':');
      if (key && value) map.set(key.trim(), value.trim());
    }
    return map;
  }

  /**
   * @param {ProductVariant} variant
   * @returns {string | null}
   */
  #sizeFromVariant(variant) {
    if (variant.option1) return String(variant.option1).trim();
    if (Array.isArray(variant.options) && variant.options[0]) return String(variant.options[0]).trim();
    return null;
  }

  /**
   * @param {HTMLOptionElement} option
   * @returns {boolean}
   */
  #isOptionSoldOut(option) {
    return option.disabled || option.dataset.available !== 'true';
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} soldOut
   */
  #setAddButtonState(button, soldOut) {
    button.disabled = soldOut;

    const addToCart = button.closest('add-to-cart-component');
    if (addToCart && 'disable' in addToCart && 'enable' in addToCart) {
      if (soldOut) {
        /** @type {{ disable: () => void }} */ (addToCart).disable();
      } else {
        /** @type {{ enable: () => void }} */ (addToCart).enable();
      }
    }
  }

  #bindSummaries() {
    for (const summary of this.summaries) {
      const select = /** @type {HTMLSelectElement | null} */ (summary.querySelector('[data-protection-select]'));
      const variantInput = /** @type {HTMLInputElement | null} */ (
        summary.querySelector('[data-protection-variant-input]')
      );
      const addButton = /** @type {HTMLButtonElement | null} */ (
        summary.querySelector('[data-protection-add-wrapper] button[type="submit"]')
      );

      select?.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const variantId = select.value;
        if (!variantId) return;
        if (variantInput) variantInput.value = variantId;

        if (addButton && option instanceof HTMLOptionElement) {
          this.#setAddButtonState(addButton, this.#isOptionSoldOut(option));
        }

        addButton?.click();
      });
    }
  }

  /**
   * @param {string} mainSize
   */
  #applyMainSize(mainSize) {
    const protectionSize = this.mapping.get(mainSize) || '';

    for (const summary of this.summaries) {
      const mainSizeEl = summary.querySelector('[data-protection-main-size]');
      if (mainSizeEl) mainSizeEl.textContent = mainSize || '';

      const mappedLine = summary.querySelector('[data-protection-mapped-line]');
      const mappedSizeEl = summary.querySelector('[data-protection-mapped-size]');
      if (mappedSizeEl) mappedSizeEl.textContent = protectionSize;
      if (mappedLine) mappedLine.toggleAttribute('hidden', !protectionSize);

      if (protectionSize) this.#preselect(summary, protectionSize);
    }
  }

  /**
   * @param {HTMLElement} summary
   * @param {string} sizeValue
   */
  #preselect(summary, sizeValue) {
    const select = /** @type {HTMLSelectElement | null} */ (summary.querySelector('[data-protection-select]'));
    const variantInput = /** @type {HTMLInputElement | null} */ (
      summary.querySelector('[data-protection-variant-input]')
    );
    const addButton = /** @type {HTMLButtonElement | null} */ (
      summary.querySelector('[data-protection-add-wrapper] button[type="submit"]')
    );
    const mappedSizeEl = summary.querySelector('[data-protection-mapped-size]');
    const soldOutEl = summary.querySelector('[data-protection-soldout]');
    if (!select) return;

    const target = String(sizeValue).trim().toLowerCase();
    const option = Array.from(select.options).find((o) => (o.dataset.sizeValue || '').trim().toLowerCase() === target);
    if (!(option instanceof HTMLOptionElement)) return;

    const soldOut = this.#isOptionSoldOut(option);

    select.value = option.value;
    if (variantInput) variantInput.value = option.value;
    if (addButton) this.#setAddButtonState(addButton, soldOut);

    if (mappedSizeEl) mappedSizeEl.classList.toggle('protection-size-summary__value--soldout', soldOut);
    if (soldOutEl) soldOutEl.toggleAttribute('hidden', !soldOut);
  }
}

if (!customElements.get('protection-upsell')) {
  customElements.define('protection-upsell', ProtectionUpsell);
}
